import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Observable, Subscription, Subject } from 'rxjs';
import { ApiPackage, ApiTraveler, ApiPackageConfiguration, ApiQuoteResponse, ApiMessage } from '@bhtp-client-api/base';

import { ModalService, Constants, CoverageCartViewModel, CoverageCartTripDetailsViewModel, StateSellingAbility } from '@bhtp-client-web/component';
import { BhtpStateService, State, BhtpRulesService, FieldRules, Rules, ConstraintRules, IFieldConfig, TravelerDetailConfig } from '@bhtp-client-api/eligibility';
import { BhtpLicenseService, AvailablePackages, PackageAgreement, PackageDetails, StateAvailability, PackageAvailability } from '@bhtp-client-api/purchase';

import { PurchaseNavigationService } from './purchase-navigation.service';
import { QuotingService } from './quoting.service';
import { NavigationConfig } from './navigation-config.model';
import { StepConfig } from './step-config.model';
import { EmailQuoteModal, EmailQuoteData } from '../email-quote';
import { Traveler, CoverageContainer, Coverage, CustomerFullData, PurchaseData, EmailQuote, Agent, PaymentFormData } from './../shared/models';
import { SubscriptionHelper, PackageAvailabilityHelper } from './../shared/helpers';
import { TramsService } from './../shared';

@Component({
    selector: 'purchase-container',
    template: require('./purchase-container.component.html')
})
export class PurchaseContainerComponent implements OnInit, OnDestroy {
    @Input() ratingId: string;
    @Input() agent: Agent;
    @Input() customer: ApiTraveler;
    @Input() step: string;
    @Input() sessionId: number;
    @Input() quoteId: string;
    @Input() loadedQuote: boolean;

    @Output() stepChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() packageChanged: EventEmitter<string> = new EventEmitter<string>();
    @Output() purchaseCompleted: EventEmitter<string> = new EventEmitter<string>();

    @ViewChild('emailQuoteComponent')
    public emailModal: EmailQuoteModal;

    private licenseMessage: string = '';
    private showLicenseMessage: boolean = false;

    private package: ApiPackage;
    private stepChangedSubscription: Subscription;
    private quoteCompletedSubscription: Subscription;
    private dataChangedSubscription: Subscription;
    private purchaseCompletedSubscription: Subscription;
    private getLicensesSubscription: Subscription;
    private states: State[] = [];
    private stateAvailability: StateSellingAbility[] = [];

    private rules: FieldRules;
    private constraints: ConstraintRules;
    private rulesAreLoading: boolean = true;
    private initializingData: boolean = false;

    private initialized: boolean = false;
    private quoting: boolean = false;

    private agreementsForAgent: PackageAgreement;
    private licensesForAgent: AvailablePackages;

    private stateIso2: string;
    private coverageCart: CoverageCartViewModel = new CoverageCartViewModel();
    private tripDetails: CoverageCartTripDetailsViewModel = new CoverageCartTripDetailsViewModel();

    constructor(private navigationService: PurchaseNavigationService, private bhtpStateService: BhtpStateService,
        private bhtpRulesService: BhtpRulesService, private licenseService: BhtpLicenseService, private modalService: ModalService,
        private quotingService: QuotingService, private tramsService: TramsService) { }

    public ngOnInit(): void {
        // when the navigation service changes the step, emit that step
        this.stepChangedSubscription = this.navigationService.stepChanged.subscribe(newStep => {
            this.stepChanged.emit(newStep);
        });

        this.quoteCompletedSubscription = this.quotingService.quoteCompleted.subscribe(newQuote => {
            this.onQuoteReceived(newQuote);
        });

        this.dataChangedSubscription = this.navigationService.dataChanged.subscribe((newData: PurchaseData) => {
            this.updateTripData(newData);
        });

        this.purchaseCompletedSubscription = this.navigationService.purchaseCompleted.subscribe(policyNumber => {
            this.purchaseCompleted.emit(policyNumber);
        });
    }

    public ngOnChanges(): void {
        if (this.ratingId && this.agent && this.agent.agentCode && !this.initializingData && !this.initialized) {
            this.initializingData = true;

            // populates data from saved quote
            if (this.quoteId) {
                this.navigationService.getQuoteByQuoteNumber(this.quoteId, this.sessionId)
                    .subscribe(() => {
                        this.initializeData();
                    });
            }
            else {
                this.initializeData();
            }
        }
    }

    public initializeData(): void {
        // get states for dropdowns
        const statesRequest: Observable<State[]> = this.bhtpStateService.getStates()
            .map((response) => {
                this.states = response;
            }).catch((error) => {
                this.modalService.showMessageModal('Error', 'Unable to retrieve list of states.');
                return Observable.empty();
            });

        let purchaseData: PurchaseData = this.currentPurchaseData();

        this.stateIso2 = purchaseData.primaryTraveler && purchaseData.primaryTraveler.address && purchaseData.primaryTraveler.address.stateOrProvince ?
            purchaseData.primaryTraveler.address.stateOrProvince : null;

        // get rules that define what fields show/required/editable
        const rulesRequest: Observable<Rules> = this.loadRules(this.ratingId, this.stateIso2);

        // get all packages the agent is setup to sell
        const agreements: Observable<PackageAgreement> = this.licenseService.getAgreements(this.agent.agentCode)
            .map(data => {
                this.agreementsForAgent = data;
                if (this.ratingId && this.agreementsForAgent && this.agreementsForAgent.packages) {
                    this.package = this.agreementsForAgent.packages.find(p => p.ratingId === this.ratingId);
                }
            })
            .catch(err => {
                this.agreementsForAgent = null;
                return Observable.empty();
            });

        // Grab the licenses for the agent. These
        // are used to check if commission will be received for the sale
        const licenses: Observable<AvailablePackages> = this.licenseService.getLicenses(this.agent.agentCode)
            .map(data => {
                this.licensesForAgent = data;
            })
            .catch(err => {
                this.licensesForAgent = null;
                return Observable.empty();
            });

        Observable.merge(statesRequest, rulesRequest, agreements, licenses)
            .subscribe(null, null, () => this.initCallsComplete());
    }

    public initCallsComplete(): void {
        this.setupStateAvailability();
        this.rulesAreLoading = false;
        this.initializeNavigation();
    }

    public ngOnDestroy(): void {
        this.stepChangedSubscription = SubscriptionHelper.unsubscribeHandler(this.stepChangedSubscription);
        this.dataChangedSubscription = SubscriptionHelper.unsubscribeHandler(this.dataChangedSubscription);
        this.quoteCompletedSubscription = SubscriptionHelper.unsubscribeHandler(this.quoteCompletedSubscription);
        this.purchaseCompletedSubscription = SubscriptionHelper.unsubscribeHandler(this.purchaseCompletedSubscription);
        this.getLicensesSubscription = SubscriptionHelper.unsubscribeHandler(this.getLicensesSubscription);
    }

    public onProductSwap(newRatingId: string): void {
        // find the new package in the available packages for the agency
        if (this.agreementsForAgent && this.agreementsForAgent.packages) {
            let newPackage: PackageDetails = this.agreementsForAgent.packages.find(p => p.ratingId === newRatingId);
            if (newPackage) {
                this.package = newPackage;

                // if there is a new package, change it in the nav service (which will requote).
                // Notify any listeners that the package changed
                this.navigationService.swapPackage(newRatingId, newPackage);
                this.packageChanged.emit(newRatingId);
            }
        }
    }

    public onEmailQuote(): void {
        let purchaseData: PurchaseData = this.currentPurchaseData();
        if (purchaseData && purchaseData.primaryTraveler && purchaseData.primaryTraveler.emailGroup && !purchaseData.primaryTraveler.emailGroup.noEmail) {
            let emailQuoteData: EmailQuoteData = new EmailQuoteData();
            emailQuoteData.primaryEmail = purchaseData.primaryTraveler.emailGroup.email;
            this.emailModal.open(emailQuoteData);
        }
    }

    public onSaveQuote(): void {
        // does not pass in email quote data since we're only saving the quote
        this.navigationService.saveQuote();
    }

    public getHandlePurchaseError(): Subject<ApiMessage[]> {
        return this.navigationService.getHandlePurchaseError();
    }

    public onPaymentSuccess(paymentFormData: PaymentFormData): void {
        if (this.tramsService.isTramsSession()) {
            this.navigationService.purchaseQuoteSkipQueue(paymentFormData);
        }
        else {
            this.navigationService.purchaseQuote(paymentFormData);
        }
    }

    public onNext(): void {
        this.navigationService.nextRequested();
    }

    public onPrevious(): void {
        this.navigationService.previousRequested();
    }

    public onStepClicked(step: StepConfig): void {
        this.navigationService.stepRequested(step);
    }

    public getSteps(): Array<StepConfig> {
        return this.navigationService.steps;
    }

    public showPriceInformation(): boolean {
        return this.navigationService.showPriceInformation();
    }

    public showNextButton(): boolean {
        return this.navigationService.showNextButton();
    }

    public showPreviousButton(): boolean {
        return this.navigationService.showPreviousButton();
    }

    public showSaveQuoteButton(): boolean {
        return this.navigationService.showSaveQuoteButton();
    }

    public showEmailQuoteButton(): boolean {
        return this.navigationService.showEmailQuoteButton();
    }

    public currentStep(): string {
        return this.navigationService.currentActiveStep();
    }

    public currentQuote(): ApiQuoteResponse {
        return this.navigationService.currentQuote;
    }

    public currentPurchaseData(): PurchaseData {
        return this.navigationService.getSessionPurchaseData();
    }

    public async sendQuote(emailQuoteData: EmailQuoteData): Promise<void> {
        await this.navigationService.emailQuote(emailQuoteData, this.agent);
    }

    public onCoveragesChanged(coverages: CoverageContainer): void {
        // Always requote FL because of fees dependent on selected coverages
        if (coverages.requiresRequote || this.stateIso2 === 'FL') {
            this.quoting = true;
            this.navigationService.quoteCurrentPurchaseData(false, true, null)
                .subscribe(result => {
                    if (result) {
                        // reconcile the selected coverages with the quoted coverages
                        if (result.coverages && result.coverages.optional && coverages.optionalCoverages) {
                            coverages.optionalCoverages.forEach(oc => {
                                let quotedCoverage = result.coverages.optional.find(qc => qc.ratingId === oc.ratingId &&
                                    qc.ratingId === Constants.COVERAGE_CFAR);
                                if (quotedCoverage) {
                                    oc.premium = quotedCoverage.premium;
                                }
                            });
                        }

                        this.updateQuoteData(result, coverages.optionalCoverages);
                    }
                }, null, () => { this.quoting = false; });
        }
        else {
            let currentQuote: ApiQuoteResponse = this.quotingService.getCurrentQuote();
            if (currentQuote) {
                this.updateQuoteData(currentQuote, coverages.optionalCoverages);
            }
        }
    }

    public onTripCostChanged(): void {
         // setup sidebar data
         let currentTripData: PurchaseData = this.currentPurchaseData();
         this.setupTripDetailsSidebar(currentTripData);
    }

    protected onPolicyHolderResidenceStateChange(state: State): void {
        if (state && state.isoCode2) {
            this.modalService.showLoadingModal(`Loading package rules for ${state.name || state.isoCode2}...`);
            this.loadRules(this.ratingId, state.isoCode2)
                .subscribe(
                    null,
                    (err) => {
                        this.modalService.hideLoadingModal();
                        this.modalService.showMessageModal('Error', `Failed to load package rules for ${state.name || state.isoCode2}.`);
                    },
                    () => {
                        this.modalService.hideLoadingModal();
                    });
        }
    }

    private loadRules(ratingId: string, stateIsoCode2: string = null): Observable<Rules> {
        return this.bhtpRulesService.getPolicyRulesets(ratingId, stateIsoCode2)
            .map(data => {
                this.rules = data.fields;
                this.constraints = data.constraints;
                this.navigationService.updateRules(this.rules, this.constraints);
            }).catch(err => {
                // this allows the default configuration to be used if rules call fails
                this.rules = null;
                this.constraints = null;
                return Observable.empty();
            });
    }

    private setupStateAvailability(): void {
        if (this.states && this.licensesForAgent && this.licensesForAgent.states) {
            this.states.forEach(s => {
                let canSell: boolean = false;
                let currentStateAvailability: StateAvailability = this.licensesForAgent.states.find(ls => ls.iso2Code === s.isoCode2);
                if (currentStateAvailability && currentStateAvailability.packages) {
                    let currentPackage: PackageAvailability = currentStateAvailability.packages.find(p => p.ratingId === this.ratingId);
                    canSell = PackageAvailabilityHelper.canSellPackage(currentPackage);
                }

                let availability: StateSellingAbility = new StateSellingAbility(s.isoCode2, s.name, canSell);
                this.stateAvailability.push(availability);
            });
        }
    }

    private initializeNavigation(): void {
        let navigationConfig: NavigationConfig = new NavigationConfig();
        navigationConfig.ratingId = this.ratingId;
        navigationConfig.package = this.package;
        navigationConfig.hasTravelerStep = true;
        navigationConfig.agreementsForAgent = this.agreementsForAgent;
        navigationConfig.sessionId = this.sessionId;
        navigationConfig.loadedQuote = this.loadedQuote;

        if (this.customer) {
            navigationConfig.customerId = this.customer.id;
        }

        if (this.package) {
            // show coverages step if package includes reimbursed coverages, and skip
            // if it only has fixed
            if (this.package.claimPayout && this.package.claimPayout.some(c => c.toLowerCase().indexOf('reimbursed') >= 0)) {
                navigationConfig.hasCoveragesStep = true;
            }

            // if the package can collect flights, show the flight step
            if (this.package.configuration && ApiPackageConfiguration.allowsFlights(this.package.configuration)) {
                navigationConfig.hasFlightStep = true;
            }
        }

        navigationConfig.hasTripStep = true;

        navigationConfig.initialStep = this.step;
        this.navigationService.setConfig(navigationConfig, this.rules, this.constraints);

        // setup sidebar data
        let currentTripData: PurchaseData = this.currentPurchaseData();
        this.setupTripDetailsSidebar(currentTripData);

        let currentQuote: ApiQuoteResponse = this.quotingService.getCurrentQuote();
        if (currentQuote) {
            this.updateQuoteData(currentQuote, currentTripData ? currentTripData.selectedCoverages : null);
        }

        if (!this.quoteId) {
            // updates the purchase data with customer data
            // only update if not loading from a quote, though.
            this.navigationService.setPrimaryTravelerToPurchaseData(this.customer);
        }

        this.finishInitialization();
    }

    private finishInitialization(): void {
        this.initialized = true;
        this.initializingData = false;
    }

    private dismissMessage(): void {
        this.showLicenseMessage = false;
    }

    private onQuotingStatusChanged(isQuoting: boolean): void {
        this.quoting = isQuoting;
    }

    private onQuoteReceived(newQuote: ApiQuoteResponse): void {
        let currentTripData: PurchaseData = this.currentPurchaseData();
        this.updateQuoteData(newQuote, currentTripData ? currentTripData.selectedCoverages : null);
    }

    private updateQuoteData(quote: ApiQuoteResponse, coverages?: Coverage[]): void {
        if (quote) {
            quote.package = this.package;
            const filteredCoverages: Coverage[] = [];
            if (coverages && coverages.length > 0) {
                coverages.forEach(c => {
                    if (quote.coverages.optional.some(quoteCoverage => quoteCoverage.ratingId === c.ratingId && quoteCoverage.coverageAvailable)) {
                        filteredCoverages.push(c);
                    }
                });
            }
            this.coverageCart = CoverageCartViewModel.ConvertApiQuoteResponseIntoCoverageCart(quote, filteredCoverages);
        }
    }

    private setupTripDetailsSidebar(currentTripData: PurchaseData) {
        if (currentTripData) {
            this.updateTripData(currentTripData);
        }
    }

    private updateTripData(data: PurchaseData): void {
        this.tripDetails.departureDate = data.departureDate;
        this.tripDetails.returnDate = data.returnDate;

        let tripCost: number = 0;
        if (data.primaryTraveler && data.primaryTraveler.tripCost) {
            let primaryTripCost: number = Number(data.primaryTraveler.tripCost);
            if (!Number.isNaN(primaryTripCost)) {
                tripCost = primaryTripCost;
            }
        }

        if (data.additionalTravelers) {
            data.additionalTravelers.forEach(t => {
                let travelerTripCost: number = Number(t.tripCost);
                if (t.tripCost && !Number.isNaN((travelerTripCost))) {
                    tripCost = tripCost + travelerTripCost;
                }
            });
        }

        this.tripDetails.tripCost = tripCost;
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/typescript/purchase/purchase-container.component.ts